/**
 * Media RSS — fetch Meltwater XML and render coverage cards.
 */

const INITIAL_COUNT = 6;

/**
 * Format an ISO/date string for display.
 *
 * @param {string} dateStr
 * @return {string}
 */
function formatDate(dateStr) {
	const date = new Date(dateStr);
	if (Number.isNaN(date.getTime())) {
		return dateStr;
	}

	return date.toLocaleDateString(undefined, {
		year: 'numeric',
		month: 'long',
		day: 'numeric',
	});
}

/**
 * Read the first matching child tag's text.
 *
 * @param {Element} parent
 * @param {string} tagName
 * @return {string}
 */
function getTagText(parent, tagName) {
	const node = parent.getElementsByTagName(tagName)[0];
	return node?.textContent?.trim() || '';
}

/**
 * Build a single media item using the shared resources-card markup
 * (body only — no media / background image).
 *
 * @param {{ title: string, url: string, publisher: string, publishDate: string }} item
 * @param {boolean} hidden
 * @return {HTMLElement}
 */
function createItemElement(item, hidden) {
	const article = document.createElement('article');
	article.className = 'resources-card media-rss__item';
	article.setAttribute('role', 'listitem');
	if (hidden) {
		article.hidden = true;
	}

	const link = document.createElement('a');
	link.className = 'resources-card__link';
	link.href = item.url;
	link.target = '_blank';
	link.rel = 'noopener noreferrer';
	link.setAttribute('aria-label', `Read: ${item.title}`);

	const body = document.createElement('span');
	body.className = 'resources-card__body';

	if (item.publisher) {
		const type = document.createElement('span');
		type.className = 'resources-card__type is-style-eyebrow media-rss__source';
		type.textContent = item.publisher;
		body.appendChild(type);
	}

	const title = document.createElement('h3');
	title.className = 'resources-card__title';
	title.textContent = item.title;
	body.appendChild(title);

	const foot = document.createElement('span');
	foot.className = 'resources-card__foot';

	const view = document.createElement('span');
	view.className = 'resources-card__cta-link';
	view.textContent = 'Read \u27F6';
	foot.appendChild(view);

	if (item.publishDate) {
		const date = document.createElement('span');
		date.className = 'resources-card__date';
		date.textContent = item.publishDate;
		foot.appendChild(date);
	}

	body.appendChild(foot);
	link.appendChild(body);
	article.appendChild(link);

	return article;
}

/**
 * Parse Meltwater XML into item objects.
 *
 * @param {string} xmlText
 * @return {Array<{ title: string, url: string, publisher: string, publishDate: string }>}
 */
function parseDocuments(xmlText) {
	const parser = new DOMParser();
	const xmlDoc = parser.parseFromString(xmlText, 'text/xml');

	if (xmlDoc.querySelector('parsererror')) {
		throw new Error('Invalid XML response');
	}

	return Array.from(xmlDoc.getElementsByTagName('document'))
		.map((doc) => {
			const title = getTagText(doc, 'title');
			const url = getTagText(doc, 'url');
			const publisher = getTagText(doc, 'sourcename') || 'Unknown Publisher';
			const publishDateRaw = getTagText(doc, 'createDate5');
			const publishDate = publishDateRaw ? formatDate(publishDateRaw) : '';

			if (!title || !url) {
				return null;
			}

			return { title, url, publisher, publishDate };
		})
		.filter(Boolean);
}

/**
 * @param {HTMLElement} root
 */
async function initBlock(root) {
	const list = root.querySelector('[data-media-rss-list]');
	const status = root.querySelector('[data-media-rss-status]');
	const actions = root.querySelector('[data-media-rss-actions]');
	const showAllBtn = root.querySelector('[data-media-rss-show-all]');
	const xmlUrl = root.getAttribute('data-xml-url') || '';
	const initialCount = Number.parseInt(root.getAttribute('data-initial-count') || '', 10) || INITIAL_COUNT;

	if (!list || !xmlUrl) {
		return;
	}

	const setStatus = (message, { isError = false } = {}) => {
		if (!status) {
			return;
		}
		status.hidden = !message;
		status.textContent = message || '';
		status.classList.toggle('media-rss__status--error', isError);
	};

	try {
		const response = await fetch(xmlUrl);
		if (!response.ok) {
			throw new Error(`Request failed (${response.status})`);
		}

		const xmlText = await response.text();
		const items = parseDocuments(xmlText);

		list.setAttribute('aria-busy', 'false');

		if (!items.length) {
			setStatus('No media coverage found.');
			return;
		}

		const fragment = document.createDocumentFragment();
		items.forEach((item, index) => {
			fragment.appendChild(createItemElement(item, index >= initialCount));
		});
		list.appendChild(fragment);

		if (items.length > initialCount && actions && showAllBtn) {
			actions.hidden = false;
			showAllBtn.addEventListener(
				'click',
				() => {
					list.querySelectorAll('.media-rss__item[hidden]').forEach((el) => {
						el.hidden = false;
					});
					actions.remove();
				},
				{ once: true }
			);
		}
	} catch (error) {
		list.setAttribute('aria-busy', 'false');
		setStatus('Unable to load media coverage.', { isError: true });
		// Keep console noise for debugging feed/CORS issues.
		console.error('Media RSS:', error);
	}
}

export function initMediaRss() {
	document.querySelectorAll('[data-media-rss]').forEach((root) => {
		initBlock(root);
	});
}
